import { useEffect, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight, Plus, Trash2, CalendarDays, MapPin } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { HR_WRITE } from "@/lib/constants";
import { HolidaysCard } from "@/components/HolidaysCard";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const MONTHS = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
const DOW = ["Min", "Sen", "Sel", "Rab", "Kam", "Jum", "Sab"];
const EVENT_TYPES = {
  EVENT: { label: "Acara", chip: "bg-primary/15 text-primary", dot: "bg-primary" },
  MEETING: { label: "Rapat", chip: "bg-sky-100 text-sky-700", dot: "bg-sky-500" },
  TRAINING: { label: "Pelatihan", chip: "bg-emerald-100 text-emerald-700", dot: "bg-emerald-500" },
  DEADLINE: { label: "Deadline", chip: "bg-amber-100 text-amber-800", dot: "bg-amber-500" },
  OTHER: { label: "Lainnya", chip: "bg-slate-100 text-slate-700", dot: "bg-slate-400" },
};

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const EMPTY = { title: "", date: "", end_date: "", type: "EVENT", location: "", description: "" };

export default function Calendar() {
  const { user } = useAuth();
  const canWrite = HR_WRITE.includes(user.role);
  const today = new Date();
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [holidays, setHolidays] = useState([]);
  const [events, setEvents] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(EMPTY);

  const load = useCallback(() => {
    api.get("/holidays").then((r) => setHolidays(r.data)).catch(() => {});
    api.get("/events").then((r) => setEvents(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const firstDow = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [...Array(firstDow).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
  while (cells.length % 7 !== 0) cells.push(null);

  const holidaysOn = (ds) => holidays.filter((h) => h.date === ds);
  const eventsOn = (ds) => events.filter((e) => ds >= e.date && ds <= (e.end_date || e.date));

  const addEvent = async () => {
    if (!f.title || !f.date) return toast.error("Judul dan tanggal wajib diisi");
    try {
      await api.post("/events", { ...f, end_date: f.end_date || null });
      toast.success("Acara ditambahkan");
      setOpen(false); setF(EMPTY); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const delEvent = async (id) => { try { await api.delete(`/events/${id}`); load(); } catch (e) { toast.error(apiErr(e)); } };

  const monthEvents = events
    .filter((e) => {
      const s = e.date, en = e.end_date || e.date;
      const mStart = ymd(new Date(year, month, 1)), mEnd = ymd(new Date(year, month, daysInMonth));
      return en >= mStart && s <= mEnd;
    })
    .sort((a, b) => a.date.localeCompare(b.date));

  return (
    <div className="space-y-6" data-testid="calendar-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight lg:text-3xl">Kalender Perusahaan</h1>
          <p className="mt-1 text-sm text-slate-500">Hari libur & acara PT Showty Global International dalam satu tampilan.</p>
        </div>
        {canWrite && (
          <Button data-testid="add-event-button" onClick={() => { setF({ ...EMPTY, date: ymd(today) }); setOpen(true); }} className="gap-2">
            <Plus className="h-4 w-4" /> Tambah Acara
          </Button>
        )}
      </div>

      <Card className="rounded-2xl border-slate-200 p-4 dark:border-slate-800 sm:p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-heading text-lg font-semibold sm:text-xl">{MONTHS[month]} {year}</h2>
          <div className="flex items-center gap-2">
            <Button size="icon" variant="outline" data-testid="calendar-prev" onClick={() => setCursor(new Date(year, month - 1, 1))}><ChevronLeft className="h-4 w-4" /></Button>
            <Button size="sm" variant="ghost" data-testid="calendar-today" onClick={() => setCursor(new Date(today.getFullYear(), today.getMonth(), 1))}>Hari ini</Button>
            <Button size="icon" variant="outline" data-testid="calendar-next" onClick={() => setCursor(new Date(year, month + 1, 1))}><ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>

        <div className="grid grid-cols-7 gap-1 text-center">
          {DOW.map((d, i) => (
            <div key={d} className={`pb-2 text-xs font-semibold ${i === 0 ? "text-rose-500" : "text-slate-400"}`}>{d}</div>
          ))}
          {cells.map((day, idx) => {
            if (!day) return <div key={idx} className="min-h-[84px] rounded-lg bg-slate-50/50 dark:bg-slate-900/30" />;
            const ds = ymd(new Date(year, month, day));
            const isToday = ds === ymd(today);
            const dayHols = holidaysOn(ds);
            const dayEvts = eventsOn(ds);
            const isSun = new Date(year, month, day).getDay() === 0;
            return (
              <div key={idx} data-testid="calendar-day" className={`min-h-[84px] rounded-lg border p-1.5 text-left transition-colors ${dayHols.length ? "border-rose-200 bg-rose-50/60 dark:border-rose-900 dark:bg-rose-950/20" : "border-slate-100 dark:border-slate-800"} ${isToday ? "ring-2 ring-primary" : ""}`}>
                <span className={`text-xs font-semibold ${isSun || dayHols.length ? "text-rose-600" : "text-slate-600 dark:text-slate-300"}`}>{day}</span>
                <div className="mt-1 space-y-0.5">
                  {dayHols.map((h) => (
                    <div key={h.id} data-testid="calendar-holiday-chip" title={h.name} className="truncate rounded bg-rose-100 px-1 py-0.5 text-[10px] font-medium text-rose-700 dark:bg-rose-950/50 dark:text-rose-300">{h.name}</div>
                  ))}
                  {dayEvts.map((e) => (
                    <div key={e.id} data-testid="calendar-event-chip" title={e.title} className={`truncate rounded px-1 py-0.5 text-[10px] font-medium ${EVENT_TYPES[e.type]?.chip || EVENT_TYPES.OTHER.chip}`}>{e.title}</div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-slate-100 pt-3 text-[11px] dark:border-slate-800">
          <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-rose-500" /> Libur</span>
          {Object.entries(EVENT_TYPES).map(([k, v]) => (
            <span key={k} className="flex items-center gap-1"><span className={`h-2.5 w-2.5 rounded-full ${v.dot}`} /> {v.label}</span>
          ))}
        </div>
      </Card>

      {/* Upcoming events this month */}
      <Card className="rounded-2xl border-slate-200 p-6 dark:border-slate-800">
        <div className="mb-4 flex items-center gap-2">
          <CalendarDays className="h-4 w-4 text-primary" />
          <h3 className="font-heading text-lg font-semibold">Acara Bulan Ini</h3>
        </div>
        {monthEvents.length === 0 && <p className="text-sm text-slate-400">Tidak ada acara di bulan ini.</p>}
        <div className="space-y-2">
          {monthEvents.map((e) => (
            <div key={e.id} data-testid="event-item" className="flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50 px-4 py-2.5 dark:border-slate-800 dark:bg-slate-800/40">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${EVENT_TYPES[e.type]?.chip || EVENT_TYPES.OTHER.chip}`}>{EVENT_TYPES[e.type]?.label || e.type}</span>
                  <span className="truncate text-sm font-medium">{e.title}</span>
                </div>
                {e.location && <p className="mt-0.5 flex items-center gap-1 text-xs text-slate-400"><MapPin className="h-3 w-3" /> {e.location}</p>}
              </div>
              <div className="flex items-center gap-2">
                <span className="whitespace-nowrap font-mono text-xs text-slate-500">{e.date}{e.end_date && e.end_date !== e.date ? ` → ${e.end_date}` : ""}</span>
                {canWrite && <Button size="icon" variant="ghost" data-testid="delete-event-button" onClick={() => delEvent(e.id)}><Trash2 className="h-4 w-4 text-rose-500" /></Button>}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {canWrite && <HolidaysCard />}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Tambah Acara</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Judul Acara</Label>
              <Input data-testid="event-title-input" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Contoh: Town Hall Q3" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Tanggal Mulai</Label>
                <Input data-testid="event-date-input" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></div>
              <div className="space-y-2"><Label>Tanggal Selesai</Label>
                <Input data-testid="event-enddate-input" type="date" value={f.end_date} onChange={(e) => setF({ ...f, end_date: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Jenis</Label>
                <Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}>
                  <SelectTrigger data-testid="event-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(EVENT_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="space-y-2"><Label>Lokasi</Label>
                <Input data-testid="event-location-input" value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} placeholder="Opsional" /></div>
            </div>
            <div className="space-y-2"><Label>Keterangan</Label>
              <Textarea data-testid="event-desc-input" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="Opsional" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button data-testid="save-event-button" onClick={addEvent}>Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
